import Vue from 'vue'

export const gatewayAlarmBus = new Vue()

let alarmWs = null
let heartbeatTimer = null
let reconnectTimer = null
let appStore = null
let alarmPriorities = null

// 当前正在弹出的报警弹窗，key=变量ID。
// 同一报警变量在一个报警周期内只弹一个，恢复后自动关闭并从表中移除。
const activeAlarmNotifications = new Map()

function notificationPriority(item) {
  return item.notification_priority == null ? 'unclassified' : String(item.notification_priority)
}

function allowsPopup(item) {
  // The server checks the latest settings across all sessions. Local choices
  // only provide compatibility for frames without the notification decision.
  if (typeof item.notify_alarm === 'boolean') return item.notify_alarm
  return alarmPriorities === null || alarmPriorities.includes(notificationPriority(item))
}

export function updateAlarmNotificationPreferences(priorities) {
  alarmPriorities = Array.isArray(priorities) ? priorities.map(String) : null
  activeAlarmNotifications.forEach((entry, id) => {
    if (alarmPriorities !== null && !alarmPriorities.includes(entry.priority)) closeAlarmNotification(id)
  })
}

function isItemAlarm(item) {
  if (item.is_alarm !== undefined) return Boolean(item.is_alarm)
  if (item.isAlarm !== undefined) return Boolean(item.isAlarm)
  return item.data_quality === 'alarm'
}

function isAlarmMessage(msg) {
  if (!msg) return false
  if (msg.type === 'alarm' || msg.type === 'alarm_event' || msg.type === 'alarm_message') return true
  if (msg.event === 'alarm' || msg.event === 'alarm_event') return true
  if (msg.alarm_event_id || msg.alarmEventId) return true
  if (msg.is_alarm !== undefined) return Boolean(msg.is_alarm)
  if (msg.isAlarm !== undefined) return Boolean(msg.isAlarm)
  return false
}

function refreshUnreadCount(delay = 0) {
  if (!appStore) {
    return
  }

  setTimeout(() => {
    appStore.dispatch('fetchUnreadCount')
  }, delay)
}

function canConnect(route) {
  const path = route && route.path
  return Boolean(localStorage.getItem('token')) && path !== '/login' && path !== '/'
}

// 通知等级 → 弹窗标题后缀。null（未分类）不加后缀，保持原标题不变。
const ALARM_PRIORITY_LABELS = { 1: '一级', 2: '二级', 3: '三级' }

function buildAlarmMessage(deviceName, item) {
  const device = deviceName || '未知设备'
  const variable = item.name || `变量ID ${item.id}`
  // 配了自定义文案：结构化上下文 + 文案本身（占位符已在后端替换好）。
  // 只加前缀、不改 alarm_text —— 驾驶舱那列旁边已有设备名/变量名两列，不能被重复。
  if (item.alarm_text) return `${device} 的 ${variable}：${item.alarm_text}`
  return `${device} 的 ${variable} 异常`
}

function buildAlarmTitle(item) {
  const label = ALARM_PRIORITY_LABELS[Number(item.notification_priority)]
  return label ? `设备变量报警 · ${label}` : '设备变量报警'
}

function prepareDesktopNotification() {
  if (!('Notification' in window) || Notification.permission !== 'default') {
    return
  }

  const permissionRequest = Notification.requestPermission()
  if (permissionRequest && typeof permissionRequest.catch === 'function') {
    permissionRequest.catch(() => {})
  }
}

function closeAlarmNotification(variableId) {
  const entry = activeAlarmNotifications.get(variableId)
  if (!entry) return
  try {
    entry.close()
  } catch (error) {
    // 通知可能已被浏览器/用户关闭，忽略
  }
  activeAlarmNotifications.delete(variableId)
}

function showAlarmNotification(deviceName, item) {
  const variableId = item.id
  if (variableId === undefined || variableId === null) return

  // 同一报警变量在报警周期内只弹一次，避免每帧上行重复弹窗堆叠
  if (activeAlarmNotifications.has(variableId)) return

  const title = buildAlarmTitle(item)
  const message = buildAlarmMessage(deviceName, item)

  if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body: message,
      tag: `alarm:${variableId}`,
      requireInteraction: true
    })
    let closeTimer = null
    const close = () => {
      if (closeTimer) {
        clearTimeout(closeTimer)
        closeTimer = null
      }
      try {
        notification.close()
      } catch (error) {
        // 通知可能已被浏览器/用户关闭，忽略
      }
    }
    // 与 el-notify 保持一致：不关闭则 5 分钟后自动收起
    closeTimer = setTimeout(close, 300000)
    notification.onclick = () => {
      window.focus()
      close()
    }
    activeAlarmNotifications.set(variableId, { close, priority: notificationPriority(item) })
    return
  }

  const notify = Vue.prototype.$notify({
    title,
    message,
    type: 'error',
    position: 'bottom-right',
    duration: 300000,
    showClose: true
  })
  activeAlarmNotifications.set(variableId, {
    priority: notificationPriority(item),
    close: () => notify.close()
  })
}

function handleGatewayMessage(msg) {
  if (!msg || msg.type === 'command_ack') {
    return
  }

  gatewayAlarmBus.$emit('gateway-realtime-message', msg)

  if (isAlarmMessage(msg)) {
    gatewayAlarmBus.$emit('message-list-refresh')
    refreshUnreadCount()
    refreshUnreadCount(1000)
  }

  const variables = Array.isArray(msg.variables) ? msg.variables : []
  let hasAlarm = false
  let hasRecoveredAlarm = false

  variables.forEach(item => {
    if (isItemAlarm(item) && allowsPopup(item)) {
      hasAlarm = true
      showAlarmNotification(msg.device_name, item)
    } else {
      // 仅当该变量本次确实出现且已恢复正常时才关闭弹窗。
      // 设备 FC01/FC03 交替上行，本帧不含的变量不代表已恢复，
      // 不能因"缺帧"而误关，否则报警弹窗会随帧交替弹入弹出。
      closeAlarmNotification(item.id)
      if (item.alarm_status === 'resolved' && item.alarm_resolved_at) {
        hasRecoveredAlarm = true
      }
    }
  })

  if (hasAlarm || hasRecoveredAlarm) {
    gatewayAlarmBus.$emit('message-list-refresh')
    refreshUnreadCount()
    refreshUnreadCount(1000)
  }
}

export function stopGatewayAlarmService() {
  alarmPriorities = null
  activeAlarmNotifications.forEach((_, variableId) => closeAlarmNotification(variableId))
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
  if (alarmWs) {
    alarmWs.onclose = null
    alarmWs.close()
    alarmWs = null
  }
}

export function startGatewayAlarmService(route) {
  if (!canConnect(route)) {
    stopGatewayAlarmService()
    return
  }

  prepareDesktopNotification()

  if (alarmWs && alarmWs.readyState !== WebSocket.CLOSED) {
    return
  }

  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
  const wsUrl = `${protocol}://${window.location.host}/api/v1/gateway/ws`

  alarmWs = new WebSocket(wsUrl)

  alarmWs.onopen = () => {
    console.log('全局报警实时通道已连接')
    const authToken = localStorage.getItem('token')
    if (authToken) {
      alarmWs.send(JSON.stringify({ type: 'auth', token: authToken }))
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    heartbeatTimer = setInterval(() => {
      if (alarmWs && alarmWs.readyState === WebSocket.OPEN) {
        alarmWs.send('ping')
      }
    }, 30000)
  }

  alarmWs.onmessage = (event) => {
    try {
      handleGatewayMessage(JSON.parse(event.data))
    } catch (error) {
      console.error('解析全局网关实时数据失败:', error)
    }
  }

  alarmWs.onerror = (error) => {
    console.error('全局报警实时通道连接失败:', error)
  }

  alarmWs.onclose = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
    alarmWs = null
    reconnectTimer = setTimeout(() => {
      startGatewayAlarmService(route)
    }, 1000)
  }
}

export function setupGatewayAlarmService(router, store) {
  appStore = store

  router.afterEach((to) => {
    startGatewayAlarmService(to)
  })

  startGatewayAlarmService(router.currentRoute)
}
